import { Stack, Text, Title } from "@mantine/core";
import { useMemo } from "react";
import {
  DictionaryManagement,
  DictionaryService,
} from "../../components/feature-specific/DictionaryManagement";
import { useLanguageDictionary } from "../../hooks/queries/useLanguageDictionary";
import {
  ConsolidateLanguagesRequest,
  LanguageCreateRequest,
  LanguageUpdateRequest,
} from "../../models/language.model";
import { LanguageDictionaryAdminService } from "../../services/admin";

interface LanguageDisplay {
  id: number;
  title: string; // For DictionaryManagement compatibility
  englishName: string;
  nativeName: string;
}

export function LanguageDictionary() {
  const languageService = useMemo(
    () => new LanguageDictionaryAdminService(),
    []
  );

  // Use React Query for reading languages
  const { data: languagesFromQuery = [] } = useLanguageDictionary();

  // Adapt LanguageDictionaryAdminService to DictionaryService interface for languages
  const dictionaryService: DictionaryService<LanguageDisplay> = useMemo(
    () => ({
      getDictionary: async () => {
        return languagesFromQuery.map((lang) => ({
          ...lang,
          title: `${lang.englishName} (${lang.nativeName})`, // Combine for title display
        }));
      },
      createItem: (item: Omit<LanguageDisplay, "id">) =>
        languageService
          .createLanguage({
            englishName: item.englishName,
            nativeName: item.nativeName,
          } as LanguageCreateRequest)
          .then((lang) => ({
            ...lang,
            title: `${lang.englishName} (${lang.nativeName})`,
          })),
      updateItem: (id: number, item: Partial<LanguageDisplay>) =>
        languageService
          .updateLanguage(id, {
            id,
            englishName: item.englishName!,
            nativeName: item.nativeName!,
          } as LanguageUpdateRequest)
          .then((lang) => ({
            ...lang,
            title: `${lang.englishName} (${lang.nativeName})`,
          })),
      deleteItem: (id: number) => languageService.deleteLanguage(id),
      consolidateItems: (sourceId: number, targetId: number) =>
        languageService
          .consolidateLanguages({
            sourceLanguageId: sourceId,
            targetLanguageId: targetId,
          } as ConsolidateLanguagesRequest)
          .then((lang) => ({
            ...lang,
            title: `${lang.englishName} (${lang.nativeName})`,
          })),
    }),
    [languageService, languagesFromQuery]
  );

  // We'll use the DictionaryManagement component's built-in rendering

  return (
    <Stack gap="md">
      <Stack gap="xs" mb="md">
        <Title order={3} size="h4" fw={600}>
          Languages
        </Title>
        <Text c="dimmed">
          Manage languages with their English and native names
        </Text>
      </Stack>

      <DictionaryManagement
        title="Languages"
        service={dictionaryService}
        itemDisplayName="Language"
        itemDisplayNamePlural="Languages"
        canConsolidate={true}
        titleFieldName="title"
        searchPlaceholder="Search languages..."
      />
    </Stack>
  );
}
